import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { toast } from "sonner";
import { Check, Crown, Users2 } from "lucide-react";

// 3-tier plan comparison + self-serve "upgrade" (no real payment processor — see
// CLAUDE.md's Plans section; POST /billing/upgrade just sets User.plan directly).
const PLAN_CARDS = [
    {
        id: "free",
        name: "Free",
        price: 0,
        tagline: "Everything you need to track day-to-day money.",
        features: [
            "Unlimited expenses & income tracking",
            "Subscriptions tracker with renewal reminders",
            "Category budgets (weekly/monthly)",
            "Transactions timeline & calendar",
        ],
    },
    {
        id: "pro",
        name: "Pro",
        price: 9.99,
        tagline: "Everything in Free, plus the tools to see the bigger picture.",
        highlight: true,
        features: [
            "Everything in Free",
            "Analytics — spending trends & category breakdowns",
            "Sinking Funds — save toward goals",
            "Investments — track brokerage, retirement & crypto",
        ],
    },
    {
        id: "family",
        name: "Family",
        price: 15.99,
        tagline: "Everything in Pro, plus sharing with the people you track money with.",
        features: [
            "Everything in Pro",
            "Add family members to your account",
            "Shared household tracking for up to 5 people",
            "Combined or \"just me\" views on every page",
        ],
    },
];

export default function Pricing() {
    const { user, upgradePlan } = useAuth();
    const { isMember, isOwner } = useHousehold();
    const navigate = useNavigate();
    const [busyPlan, setBusyPlan] = useState(null);

    const currentPlan = user?.plan || "free";

    const choose = async (planId) => {
        if (planId === currentPlan) return;
        setBusyPlan(planId);
        try {
            await upgradePlan(planId);
            toast.success(`You're now on the ${PLAN_CARDS.find(p => p.id === planId)?.name} plan`);
            if (planId === "free" || planId === "pro") navigate("/settings?tab=plan");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't change plan");
        } finally {
            setBusyPlan(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="pricing-page">
            <div className="text-center max-w-xl mx-auto">
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Plans & pricing</h1>
                <p className="text-sm text-slate-500 mt-2">
                    Start free. Upgrade any time — no real payment is processed yet, so this is safe to try.
                </p>
                {isMember && !isOwner && (
                    <p className="text-xs text-indigo-600 bg-indigo-50 rounded-xl px-3 py-2 mt-4 inline-block">
                        You're on the Family plan already, inherited from your household owner.
                    </p>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
                {PLAN_CARDS.map(plan => {
                    const isCurrent = plan.id === currentPlan;
                    return (
                        <div
                            key={plan.id}
                            data-testid={`plan-card-${plan.id}`}
                            className={`relative rounded-2xl p-6 flex flex-col ${
                                plan.highlight
                                    ? "card-elev border-2 border-indigo-500 shadow-lg shadow-indigo-500/10"
                                    : "card-elev"
                            }`}
                        >
                            {plan.highlight && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-indigo-600 text-white text-[11px] font-bold uppercase tracking-wide">
                                    Most popular
                                </span>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                                {plan.id === "family" && <Users2 className="w-4 h-4 text-indigo-600" />}
                                {plan.id === "pro" && <Crown className="w-4 h-4 text-amber-500" />}
                                <h2 className="font-display text-lg font-bold text-slate-900">{plan.name}</h2>
                            </div>
                            <div className="mb-2">
                                <span className="font-display text-3xl font-extrabold text-slate-900">
                                    {plan.price === 0 ? "$0" : `$${plan.price.toFixed(2)}`}
                                </span>
                                {plan.price > 0 && <span className="text-sm text-slate-500">/mo</span>}
                            </div>
                            <p className="text-sm text-slate-500 mb-5">{plan.tagline}</p>
                            <ul className="space-y-2.5 mb-6 flex-1">
                                {plan.features.map(f => (
                                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                                        <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => choose(plan.id)}
                                disabled={isCurrent || busyPlan === plan.id}
                                data-testid={`plan-choose-${plan.id}`}
                                className={`w-full h-11 rounded-xl text-sm font-semibold transition-all ${
                                    isCurrent
                                        ? "bg-slate-100 text-slate-400 cursor-default"
                                        : plan.highlight
                                            ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                                            : "bg-slate-900 hover:bg-slate-800 text-white"
                                }`}
                            >
                                {isCurrent ? "Current plan" : busyPlan === plan.id ? "Updating…" : plan.price === 0 ? "Downgrade to Free" : `Choose ${plan.name}`}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
